import { z } from 'zod';
import { IsoTime, ServerId, Severity } from './basics.js';
import { ServerGroupId } from './server.js';
import { Email } from './user.js';
/**
 * Notifications (knowledge doc sections 15, 16 and 20; P9.1, Slack, Telegram and
 * the daily summary P9.3).
 *
 * Section 20 orders the channels email, then Slack, then Telegram. A **rule** says
 * who hears about which events, or who receives the daily summary: a channel, its
 * recipients, a minimum severity, optional filters on server, group and category,
 * and a rate limit in messages per hour. Events over the limit wait and go out
 * together in the next message, so a burst is one email saying "and 40 more",
 * never forty emails and never none.
 *
 * Messages are plain text on every channel, and carry what an event says about itself — its
 * severity, server, message, source address, path and time — and a link to it.
 * Never `raw`: what Wazuh or CrowdSec produced stays in the dashboard.
 */
export const NotificationChannel = z.enum(['email', 'slack', 'telegram']);
/**
 * What a rule sends: messages about events as they arrive, or the daily summary
 * (P9.3). A summary rule picks only a channel and recipients: the summary is the
 * fleet's.
 */
export const NotificationRuleKind = z.enum(['events', 'daily_summary']);
/**
 * A Slack incoming webhook (P9.3). The URL is the credential — anyone holding it
 * can post to the channel — so it is sealed with the rule and shown afterwards
 * only masked (`maskSlackWebhook`). The host is fixed: a rule cannot be pointed
 * anywhere but Slack.
 */
export const SLACK_WEBHOOK = /^https:\/\/hooks\.slack\.com\/services\/[A-Z0-9]{2,20}\/[A-Z0-9]{2,20}\/[A-Za-z0-9]{8,64}$/;
/** What a stored webhook looks like on the way out; sent back unchanged, it keeps the stored one. */
export const SLACK_WEBHOOK_MASKED = /^https:\/\/hooks\.slack\.com\/services\/[A-Z0-9]{2,20}\/[A-Z0-9]{2,20}\/…[A-Za-z0-9]{4}$/;
export const maskSlackWebhook = (url) => {
    const secret = url.slice(url.lastIndexOf('/') + 1);
    return `${url.slice(0, url.lastIndexOf('/') + 1)}…${secret.slice(-4)}`;
};
/** A Telegram chat: a numeric id (negative for groups) or a public channel's @name. */
export const TELEGRAM_CHAT = /^(?:-?\d{1,20}|@[A-Za-z][A-Za-z0-9_]{4,31})$/;
/** How many recipients a rule of each channel may have. */
export const RECIPIENTS_MAX = {
    email: 10,
    slack: 5,
    telegram: 10,
};
/** Why one recipient does not suit a channel, or `undefined`. */
export function recipientProblem(channel, value) {
    switch (channel) {
        case 'email':
            return Email.safeParse(value).success ? undefined : 'Not an email address';
        case 'slack':
            return SLACK_WEBHOOK.test(value) || SLACK_WEBHOOK_MASKED.test(value)
                ? undefined
                : 'Not a Slack webhook (https://hooks.slack.com/services/…)';
        case 'telegram':
            return TELEGRAM_CHAT.test(value)
                ? undefined
                : 'Not a Telegram chat id (a number, or @channelname)';
    }
}
/**
 * How the API talks to the mail server. `tls` is TLS from the first byte (usually
 * port 465); `starttls` requires the upgrade (587) and refuses a server that does
 * not offer it; `none` is only for a relay on the central machine itself, because
 * a password sent to anything else would cross the network in the clear.
 */
export const SmtpSecurity = z.enum(['tls', 'starttls', 'none']);
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);
export const isLoopbackHost = (host) => LOOPBACK_HOSTS.has(host.toLowerCase()) || /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host);
/** A name or an address; nothing that could be a URL, a path or two hosts. */
export const SmtpHost = z
    .string()
    .trim()
    .min(1)
    .max(253)
    .regex(/^(?:[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*|\[?[0-9A-Fa-f:.]+\]?)$/, 'A host name or address');
const EmailServerFields = z.object({
    host: SmtpHost,
    port: z.number().int().min(1).max(65_535),
    security: SmtpSecurity,
    /** `null` for a server that takes mail without signing in (a local relay). */
    username: z.string().min(1).max(254).nullable(),
    /** The sender address on every message. */
    from: Email,
});
/** The parts of the email settings that are not secret, as stored and shown. */
export const EmailServerSettings = EmailServerFields.refine((settings) => settings.security !== 'none' || isLoopbackHost(settings.host), {
    message: 'Without TLS, only a mail relay on this machine (localhost) is allowed',
    path: ['security'],
});
/**
 * `PUT /notifications/channels/email`. The password is write-only: left out, the
 * stored one stays; `null` clears it; a string replaces it.
 */
export const UpdateEmailChannelRequest = EmailServerFields.extend({
    password: z.string().min(1).max(500).nullable().optional(),
})
    .strict()
    .refine((settings) => settings.security !== 'none' || isLoopbackHost(settings.host), {
    message: 'Without TLS, only a mail relay on this machine (localhost) is allowed',
    path: ['security'],
});
export const EmailChannelView = z.object({
    host: z.string(),
    port: z.number().int(),
    security: SmtpSecurity,
    username: z.string().nullable(),
    from: z.email(),
    /** Whether a password is stored. The password itself is never returned. */
    hasPassword: z.boolean(),
    updatedAt: IsoTime,
    updatedByEmail: z.email().nullable(),
});
/**
 * A Telegram bot's token (P9.3): `123456789:AA…`, from @BotFather. Sealed like the
 * email password and never shown again; it travels in the request path to
 * api.telegram.org, so it is scrubbed from anything kept.
 */
export const TelegramBotToken = z
    .string()
    .trim()
    .regex(/^\d{5,15}:[A-Za-z0-9_-]{30,64}$/, 'A bot token from @BotFather, like 123456789:AA…');
export const UpdateTelegramChannelRequest = z.object({ botToken: TelegramBotToken }).strict();
export const TelegramChannelView = z.object({
    /** As Telegram named the bot when the token was saved; `null` if it could not be asked. */
    botUsername: z.string().nullable(),
    updatedAt: IsoTime,
    updatedByEmail: z.email().nullable(),
});
export const TestTelegramRequest = z
    .object({ chatId: z.string().trim().regex(TELEGRAM_CHAT, 'A chat id or @channelname') })
    .strict();
/** `GET /notifications/channels`: `null` for a channel nobody has set up. Slack has no settings of its own. */
export const NotificationChannels = z.object({
    email: EmailChannelView.nullable(),
    telegram: TelegramChannelView.nullable(),
});
/** `POST /notifications/channels/email/test`: one message to one address. */
export const TestEmailRequest = z.object({ to: Email }).strict();
export const NotificationTestResult = z.object({ ok: z.literal(true) });
/** Which events a rule is about, beyond its severity. Each list is "any of these". */
export const NotificationFilters = z
    .object({
    serverIds: z.array(ServerId).max(200).optional(),
    groupIds: z.array(ServerGroupId).max(100).optional(),
    categories: z.array(z.string().trim().min(1).max(32)).max(50).optional(),
})
    .strict();
export const NOTIFICATION_RATE_LIMIT_DEFAULT = 6;
export const NOTIFICATION_RATE_LIMIT_MAX = 60;
/** The most any channel takes; `RECIPIENTS_MAX` says each one's. */
export const NOTIFICATION_RECIPIENTS_MAX = 10;
export const NotificationRuleInput = z
    .object({
    name: z.string().trim().min(1).max(100),
    kind: NotificationRuleKind.default('events'),
    channel: NotificationChannel,
    /** Addresses, Slack webhooks or Telegram chats, as the channel says. */
    recipients: z.array(z.string().trim().min(1).max(300)).min(1).max(NOTIFICATION_RECIPIENTS_MAX),
    minSeverity: Severity.default('high'),
    filters: NotificationFilters.default({}),
    rateLimit: z
        .number()
        .int()
        .min(1)
        .max(NOTIFICATION_RATE_LIMIT_MAX)
        .default(NOTIFICATION_RATE_LIMIT_DEFAULT),
    enabled: z.boolean().default(true),
})
    .strict()
    .superRefine((rule, ctx) => {
    if (rule.recipients.length > RECIPIENTS_MAX[rule.channel]) {
        ctx.addIssue({
            code: 'custom',
            path: ['recipients'],
            message: `At most ${String(RECIPIENTS_MAX[rule.channel])} for ${rule.channel}`,
        });
    }
    rule.recipients.forEach((recipient, index) => {
        const problem = recipientProblem(rule.channel, recipient);
        if (problem !== undefined) {
            ctx.addIssue({ code: 'custom', path: ['recipients', index], message: problem });
        }
    });
    if (new Set(rule.recipients).size !== rule.recipients.length) {
        ctx.addIssue({ code: 'custom', path: ['recipients'], message: 'Each at most once' });
    }
    const { serverIds, groupIds, categories } = rule.filters;
    const filtered = [serverIds, groupIds, categories].some((list) => list !== undefined && list.length > 0);
    if (rule.kind === 'daily_summary' && filtered) {
        ctx.addIssue({
            code: 'custom',
            path: ['filters'],
            message: 'The daily summary covers the whole fleet; a summary rule takes no filters',
        });
    }
});
export const NotificationRuleId = z.uuid();
export const NotificationRule = z.object({
    id: NotificationRuleId,
    name: z.string(),
    kind: NotificationRuleKind,
    channel: NotificationChannel,
    /** Slack webhooks masked (`maskSlackWebhook`); addresses and chats as they are. */
    recipients: z.array(z.string()),
    minSeverity: Severity,
    filters: NotificationFilters,
    rateLimit: z.number().int(),
    enabled: z.boolean(),
    createdAt: IsoTime,
    updatedAt: IsoTime,
    /** Events matched and still waiting to go out. */
    pending: z.number().int().nonnegative(),
});
/** One message attempted, as the recent list shows it. */
export const NotificationMessage = z.object({
    id: z.uuid(),
    ruleId: NotificationRuleId,
    ruleName: z.string(),
    kind: z.enum(['events', 'test', 'summary']),
    eventCount: z.number().int().nonnegative(),
    status: z.enum(['sent', 'failed']),
    /** What the mail server, Slack or Telegram said, shortened; never a credential. */
    error: z.string().nullable(),
    createdAt: IsoTime,
});
export const NotificationMessageQuery = z.object({
    rule_id: NotificationRuleId.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** When the daily summary is made: an hour of the day in UTC (section 20). */
export const NotificationSettingsInput = z
    .object({ summaryHour: z.number().int().min(0).max(23) })
    .strict();
export const NotificationSettingsView = NotificationSettingsInput.extend({
    updatedAt: IsoTime,
    updatedByEmail: z.email().nullable(),
});
//# sourceMappingURL=notifications.js.map